// src/context/MarketContext.jsx
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";

const KEY = "market_pref";
const DEFAULT = { country: "GB", language: "EN_GB" };

// Whitelist some common countries you’re likely to support.
// Anything outside will fall back to DEFAULT.
const ALLOWED_COUNTRIES = new Set([
    "GB","IE","US","CA","AU","NZ","FR","DE","ES","IT","NL","SE","NO","DK","FI","PT","BE","AT","CH",
    "JP","KR"
])

// Export a usable array for UI components (sorted)
export const ALLOWED_COUNTRIES_ARRAY = Array.from(ALLOWED_COUNTRIES).sort();

const normalizeCountry = (c) => {
  if (!c || typeof c !== "string") return DEFAULT.country;
  const up = c.trim().toUpperCase();
  return ALLOWED_COUNTRIES.has(up) ? up : DEFAULT.country;
};

// Accepts "en", "en-GB", "en_GB", etc → "EN_GB" or "EN"
const normalizeLanguage = (l) => {
  if (!l || typeof l !== "string") return DEFAULT.language;
  const t = l.trim().replace("-", "_").toUpperCase();
  // Prefer regioned language if present; else keep base (e.g., EN)
  // Shopify accepts LanguageCode with or without region. EN_GB / EN_US are fine internally.
  return t.match(/^[A-Z]{2}(_[A-Z]{2})?$/) ? t : DEFAULT.language;
};

// SAFE localStorage read (guarded for SSR)
const fromStorage = () => {
  if (typeof window === "undefined" || !window.localStorage) return null;
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return {
      country: normalizeCountry(parsed?.country),
      language: normalizeLanguage(parsed?.language),
    };
  } catch {
    return null;
  }
};

// Very light “best guess” if nothing stored yet
const detectFromNavigator = () => {
  if (typeof navigator === "undefined") return DEFAULT;
  const lang = navigator.language || navigator.userLanguage || "en-GB";
  const [base, regionRaw] = lang.split(/[-_]/);
  const region = (regionRaw || "").toUpperCase();
  // Map common regions to country; fallback GB
  // NOTE: this uses normalizeCountry which will fall back to DEFAULT.country if unknown.
  const countryGuess = normalizeCountry(region || (base?.toUpperCase() === "EN" ? "US" : "GB"));
  const languageGuess = normalizeLanguage(`${base}_${countryGuess}`);
  return { country: countryGuess, language: languageGuess };
};

const MarketContext = createContext(undefined);

export function MarketProvider({ children }) {
  // 1) Initial state: from storage, else detect, else default
  const initial = fromStorage() || detectFromNavigator() || DEFAULT;
  const [market, setMarketState] = useState(initial);

  // keep a ref to the latest market for use in event listeners without re-attaching
  const marketRef = useRef(market);
  useEffect(() => {
    marketRef.current = market;
  }, [market]);

  // guard to avoid double-writing during first render
  const didMount = useRef(false);

  // 2) Persist to localStorage (only after first mount)
  useEffect(() => {
    if (typeof window === "undefined" || !window.localStorage) return;
    if (!didMount.current) {
      didMount.current = true;
      return;
    }
    try {
      window.localStorage.setItem(KEY, JSON.stringify(market));
    } catch {}
  }, [market]);

  // 3) Cross-tab sync
  useEffect(() => {
    if (typeof window === "undefined") return;

    const onStorage = (e) => {
      if (e.key !== KEY) return;
      const next = fromStorage();
      if (!next) return;
      // Only update if changed
      const cur = marketRef.current || {};
      if (next.country !== cur.country || next.language !== cur.language) {
        setMarketState(next);
      }
    };

    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []); // attach once

  // 4) Helper setters (normalized)
  const setCountry = (c) =>
    setMarketState((m) => ({ ...m, country: normalizeCountry(c) }));
  const setLanguage = (l) =>
    setMarketState((m) => ({ ...m, language: normalizeLanguage(l) }));

  const resetMarket = () => setMarketState(DEFAULT);
  const detectMarketNow = () => setMarketState(detectFromNavigator());

  // keep the public API named `setMarket` for compatibility, but normalize input
  const setMarket = (m) => {
    if (typeof m === "function") {
      setMarketState((prev) => {
        const next = m(prev);
        return {
          country: normalizeCountry(next?.country),
          language: normalizeLanguage(next?.language),
        };
      });
    } else {
      setMarketState({
        country: normalizeCountry(m?.country),
        language: normalizeLanguage(m?.language),
      });
    }
  };

  const value = useMemo(
    () => ({
      market,
      setMarket,
      setCountry,
      setLanguage,
      resetMarket,
      detectMarketNow,
    }),
    [market] // safe because helpers are stable (defined above)
  );

  return (
    <MarketContext.Provider value={value}>
      {children}
    </MarketContext.Provider>
  );
}

// Safe hook: won’t crash if provider missing (still log a warning)
export const useMarket = () => {
  const ctx = useContext(MarketContext);
  if (!ctx) {
    if (process.env.NODE_ENV !== "production") {
      console.warn("useMarket used outside <MarketProvider>. Falling back to defaults.");
    }
    return {
      market: DEFAULT,
      setMarket: () => {},
      setCountry: () => {},
      setLanguage: () => {},
      resetMarket: () => {},
      detectMarketNow: () => {},
    };
  }
  return ctx;
};

/**
 * Helper to format market values for Shopify (or other APIs).
 * - Keeps country as ISO2 uppercase (e.g., GB)
 * - Converts language from EN_GB -> en-gb (Shopify-friendly locale)
 */
export const formatForShopify = (marketObj = { country: DEFAULT.country, language: DEFAULT.language }) => {
  const country_code = normalizeCountry(marketObj.country);
  const locale = normalizeLanguage(marketObj.language).replace("_", "-").toLowerCase(); // EN_GB -> en-gb
  return { country_code, locale };
};

export default MarketContext;
